import type { DataISO, Evento, GiornoCalendario } from '../api/tipi.ts';
import { giorniDellIntervallo } from '../calendario/griglia.ts';

/**
 * Costruzione del mini calendario mensile.
 *
 * Logica pura, separata dal componente: decidere quali giorni entrano nella
 * griglia e in che stato si trovano e' la parte che vale la pena verificare
 * con un test, molto piu' semplice che ispezionare il DOM.
 */

export type StatoGiorno = 'approvato' | 'in-attesa' | 'nessuno';

export interface GiornoMese {
  data: DataISO;
  giorno: number;
  /** Falso per i giorni di riempimento del mese precedente o successivo. */
  nelMese: boolean;
  weekend: boolean;
  festivita: string | null;
  stato: StatoGiorno;
  evento: Evento | null;
}

/** Lunedi' come primo giorno, come nel calendario italiano. */
const GIORNI_SETTIMANA = ['L', 'M', 'M', 'G', 'V', 'S', 'D'];

export function intestazioniSettimana(): readonly string[] {
  return GIORNI_SETTIMANA;
}

export function nomeMese(anno: number, mese: number): string {
  return new Date(Date.UTC(anno, mese - 1, 1)).toLocaleDateString('it-IT', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * La griglia del mese, completata con i giorni delle settimane a cavallo.
 *
 * Restituisce sempre settimane intere di sette giorni: senza i riempimenti la
 * prima riga risulterebbe disallineata rispetto alle intestazioni.
 */
export function griglieMese(
  anno: number,
  mese: number,
  eventiPerGiorno: ReadonlyMap<DataISO, Evento>,
  giorniInfo: ReadonlyMap<DataISO, GiornoCalendario>,
): GiornoMese[][] {
  const primo = new Date(Date.UTC(anno, mese - 1, 1));
  const ultimo = new Date(Date.UTC(anno, mese, 0));

  // getUTCDay: 0 = domenica. Riportato a 0 = lunedi'.
  const scarto = (primo.getUTCDay() + 6) % 7;
  const inizio = new Date(primo.getTime() - scarto * 86_400_000);

  const scartoFine = (7 - ((ultimo.getUTCDay() + 6) % 7) - 1) % 7;
  const fine = new Date(ultimo.getTime() + scartoFine * 86_400_000);

  const giorni = giorniDellIntervallo(
    inizio.toISOString().slice(0, 10),
    fine.toISOString().slice(0, 10),
  ).map<GiornoMese>((data) => {
    const evento = eventiPerGiorno.get(data) ?? null;
    const info = giorniInfo.get(data);
    return {
      data,
      giorno: Number(data.slice(8, 10)),
      nelMese: Number(data.slice(5, 7)) === mese,
      weekend: info?.weekend ?? eWeekend(data),
      festivita: info?.festivita ?? null,
      stato: statoDi(evento),
      evento,
    };
  });

  const settimane: GiornoMese[][] = [];
  for (let i = 0; i < giorni.length; i += 7) settimane.push(giorni.slice(i, i + 7));
  return settimane;
}

function statoDi(evento: Evento | null): StatoGiorno {
  if (!evento) return 'nessuno';
  if (evento.stato === 'APPROVATO' || evento.stato === 'REGISTRATO') return 'approvato';
  if (evento.stato === 'IN_ATTESA') return 'in-attesa';
  return 'nessuno';
}

function eWeekend(data: DataISO): boolean {
  return new Date(`${data}T00:00:00Z`).getUTCDay() % 6 === 0;
}

/**
 * Le assenze future, in ordine cronologico.
 *
 * E' la seconda cosa che serve in home dopo il calendario: il calendario dice
 * "quando", l'elenco dice "cosa" e soprattutto "a che punto e'".
 */
export function prossimeAssenze(
  eventi: readonly Evento[],
  oggi: DataISO,
  quante = 5,
): Evento[] {
  return eventi
    .filter((e) => e.dataFine >= oggi && e.stato !== 'ANNULLATO' && e.stato !== 'RIFIUTATO')
    .sort((a, b) => a.dataInizio.localeCompare(b.dataInizio))
    .slice(0, quante);
}

/**
 * Le richieste rifiutate ancora da leggere.
 *
 * Stanno fuori da `prossimeAssenze` perche' non sono assenze: quel giorno la
 * persona sara' al lavoro. Restano visibili solo finche' il giorno chiesto
 * non e' passato: e' il periodo in cui l'avviso serve ad AGIRE, chi ha avuto
 * un no deve sapere che quel giorno si presenta al lavoro, non che e' in
 * ferie. Una volta passato il giorno l'informazione non cambia piu' cosa fare,
 * e un avviso vecchio in home e' solo rumore da scorrere.
 *
 * Una versione precedente lo teneva acceso anche una settimana dopo il
 * rifiuto, oltre il giorno stesso, per chi era assente quando e' arrivato e
 * rischiava di non vederlo mai. Tolto di proposito (decisione presa col
 * committente): si preferisce quel caso raro a un elenco di rifiuti vecchi
 * che nessuno guarda piu'.
 */
export function richiesteRifiutate(eventi: readonly Evento[], oggi: DataISO): Evento[] {
  return eventi
    .filter((e) => e.stato === 'RIFIUTATO' && e.dataFine >= oggi)
    .sort((a, b) => b.dataInizio.localeCompare(a.dataInizio));
}

/** Quante richieste sono ancora in attesa di approvazione. */
export function contaInAttesa(eventi: readonly Evento[], oggi: DataISO): number {
  return eventi.filter((e) => e.stato === 'IN_ATTESA' && e.dataFine >= oggi).length;
}

export function formattaIntervallo(dal: DataISO, al: DataISO): string {
  const g = (d: DataISO) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;
  if (dal === al) return `${g(dal)}/${dal.slice(0, 4)}`;
  return `${g(dal)} – ${g(al)}/${al.slice(0, 4)}`;
}

/** Giorni di calendario coperti da un evento, estremi inclusi. */
export function durataInGiorni(evento: Evento): number {
  return giorniDellIntervallo(evento.dataInizio, evento.dataFine).length;
}
